import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { api, openPdf } from "./api";
import { CardFooter, ErrorBanner, GhostButton, OptionTile, OutlineButton, PrimaryButton, StepCard, SubHeading } from "./formParts";
import { INK, SCRIPT_FONT, initialsOf, signatureImages, useScriptFont } from "./typedSignature";
import { Icon, T } from "./signupDesign";

const STEP_LABELS = { business: "Basic info", legal: "Legal entity", processing: "Card processing", owners: "Owners", bank: "Payout bank", documents: "Documents", terms: "Fees & terms" };

const script = { fontFamily: `"${SCRIPT_FONT}", cursive`, fontWeight: 600, color: INK, lineHeight: 1.25, overflowWrap: "anywhere" };

const HOW_IT_WORKS = [
  "Fill in steps 1–7",
  "Your details are filled into the agreement",
  "Check the box and select Sign and submit",
];

/** A small stack of pages (decorative). */
function PaperStack() {
  const sheet = { position: "absolute", width: 44, height: 56, borderRadius: "6px", background: "#fff", border: `1px solid ${T.cardBorder}` };
  return (
    <Box aria-hidden="true" sx={{ position: "relative", width: 54, height: 64, flexShrink: 0 }}>
      <Box sx={{ ...sheet, left: 10, top: 0, background: T.fill }} />
      <Box sx={{ ...sheet, left: 5, top: 4 }} />
      <Box sx={{ ...sheet, left: 0, top: 8, p: "9px 8px", display: "flex", flexDirection: "column", gap: "5px", boxShadow: "0 2px 6px rgba(28,25,23,.06)" }}>
        <Box sx={{ height: 3, width: "70%", borderRadius: 2, background: T.red }} />
        {[100, 90, 100, 60].map((w, i) => <Box key={i} sx={{ height: 3, width: `${w}%`, borderRadius: 2, background: T.track }} />)}
      </Box>
    </Box>
  );
}

function HowSigningWorks() {
  return (
    <Box component="ol" aria-label="How signing works" sx={{ listStyle: "none", m: 0, p: 0, mb: 1, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
      {HOW_IT_WORKS.map((text, i) => (
        <Box component="li" key={text} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", p: 2, borderRadius: "16px", background: T.footerBg, border: `1px solid ${T.cardBorder}` }}>
          <Box aria-hidden="true" sx={{ width: 26, height: 26, borderRadius: "50%", background: T.redTint, color: T.redTextStrong, fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {i + 1}
          </Box>
          <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: T.text, lineHeight: 1.45 }}>{text}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Review and sign, like accepting terms and conditions. The agreement is
 * filled in from the application on the server; the signer can open it, and
 * checking the one box signs it: their name in a script font goes on the
 * signature lines and their initials on every page.
 */
export default function SignStep({ view, meta, token, preview: readOnly, onSaved, onBack, onGoTo, reloadMeta }) {
  const fontReady = useScriptFont();
  const owner = (view.application.owners || []).find((o) => o.isSigner) || {};
  const name = [owner.first, owner.middle, owner.last].filter(Boolean).join(" ");
  const legalName = view.application.legal.legalName;
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);

  // Before an application exists there's a sample (today's fees, blank
  // details); after, the agreement filled in from the application so far.
  const review = async () => {
    setOpening(true);
    setError(null);
    try {
      if (readOnly) await openPdf("/agreement/sample", null, "RushMyFood-Merchant-Agreement-sample.pdf");
      else await openPdf("/agreement/preview", token, "RushMyFood-Merchant-Agreement.pdf");
    } catch (e) {
      setError(e.message);
    } finally {
      setOpening(false);
    }
  };

  const agreementCard = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: 2, sm: 2.5 }, border: `1px solid ${T.cardBorder}`, borderRadius: "18px", mb: 2, flexWrap: "wrap", background: T.card }}>
      <PaperStack />
      <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: 17, color: T.text }}>Master Merchant Services Agreement</Typography>
        <Typography sx={{ color: T.hint, fontSize: 14, mt: 0.25, lineHeight: 1.5 }}>
          8 pages, including Schedule A (fees) and Schedule B (bank authorization){legalName ? `, for ${legalName}` : ""}
        </Typography>
      </Box>
      <OutlineButton onClick={review} disabled={opening} startIcon={opening ? null : <Icon.Eye size={18} />}>
        {opening ? <CircularProgress size={18} sx={{ color: T.red }} /> : "Review the agreement"}
      </OutlineButton>
    </Box>
  );

  if (!readOnly && view.missing && view.missing.length) {
    return (
      <StepCard title="Review and sign" subtitle="Read the agreement any time. A few things are still needed before you can sign it.">
        <ErrorBanner error={error} />
        {agreementCard}
        <HowSigningWorks />
        <SubHeading>Still needed</SubHeading>
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, border: `1px solid ${T.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
          {view.missing.map((m, i) => (
            <Box component="li" key={m.message} sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", px: 2, py: 1.25, borderTop: i ? `1px solid ${T.divider}` : 0 }}>
              <Box sx={{ color: T.redText, display: "flex" }}><Icon.Alert size={18} /></Box>
              <Typography sx={{ flex: "1 1 200px", fontSize: 15, color: T.text }}>{m.message}</Typography>
              <Button
                onClick={() => onGoTo(m.step)}
                endIcon={<Icon.ArrowRight size={16} />}
                sx={{ color: T.link, fontWeight: 600, px: 1.25, "&:hover": { color: T.linkHover, background: T.redTint } }}
              >
                Go to {STEP_LABELS[m.step] || m.step}
              </Button>
            </Box>
          ))}
        </Box>
        {/* Sign and submit shows here only as unavailable; signing is the branch below. */}
        <CardFooter
          left={<GhostButton onClick={onBack} startIcon={<Icon.ArrowLeft size={16} />}>Back</GhostButton>}
          right={<PrimaryButton disabled>Sign and submit</PrimaryButton>}
          hint="Available once your application is filled in"
        />
      </StepCard>
    );
  }

  const sign = async () => {
    setBusy(true);
    setError(null);
    setDetails(null);
    try {
      const { signature, initials } = signatureImages(name);
      const res = await api("POST", "/sign", {
        token,
        body: {
          agreeToSign: true,
          signerName: name,
          signature,
          initials,
          // The Schedule A fees shown on Fees & terms; the server refuses if they changed.
          feesVersion: (view.fees && view.fees.version) || (meta && meta.feesVersion),
          consentAt: view.application.terms && view.application.terms.acceptedAt,
        },
      });
      onSaved(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e.message);
      if (e.details && e.details.feesChanged) {
        // Load the new fees; the box has to be checked again after reviewing them.
        if (reloadMeta) await reloadMeta();
        setAgreed(false);
      } else {
        setDetails(e.details);
      }
    } finally {
      setBusy(false);
    }
  };

  const sigBox = { p: 2, background: T.footerBg, border: `1px solid ${T.cardBorder}`, borderRadius: "16px" };

  return (
    <StepCard
      title="Review and sign"
      subtitle={readOnly
        ? "Read the agreement now. Once your application is filled in, it's completed with your details, and you sign here: check the box, then Sign and submit."
        : "Your Master Merchant Services Agreement is filled in with the details from your application. Review it, then check the box and press Sign and submit."}
    >
      <ErrorBanner error={error} details={details} />

      {agreementCard}
      <HowSigningWorks />

      <SubHeading>Your signature</SubHeading>
      {!readOnly && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 1.5, mb: 2 }}>
          <Box sx={sigBox}>
            <Typography sx={{ color: T.hint, fontSize: 13 }}>Your signature</Typography>
            <Typography sx={{ ...script, fontSize: { xs: "1.8rem", sm: "2.2rem" }, opacity: fontReady ? 1 : 0.4 }}>{name}</Typography>
            <Typography sx={{ color: T.text2, fontSize: 14 }}>{name}{owner.title ? `, ${owner.title}` : ""}</Typography>
          </Box>
          <Box sx={sigBox}>
            <Typography sx={{ color: T.hint, fontSize: 13 }}>Initials, on every page</Typography>
            <Typography sx={{ ...script, fontSize: { xs: "1.8rem", sm: "2.2rem" }, opacity: fontReady ? 1 : 0.4 }}>{initialsOf(name)}</Typography>
          </Box>
        </Box>
      )}

      <OptionTile
        checked={agreed}
        disabled={readOnly}
        onChange={(e) => setAgreed(e.target.checked)}
        label={<Box component="span" sx={{ fontWeight: 400, color: T.text2, lineHeight: 1.65 }}>{view.signConsent}</Box>}
      />

      <CardFooter
        left={onBack ? <GhostButton onClick={onBack} startIcon={<Icon.ArrowLeft size={16} />}>Back</GhostButton> : null}
        right={(
          <PrimaryButton onClick={sign} busy={busy} disabled={readOnly || !agreed || busy || !fontReady}>
            Sign and submit
          </PrimaryButton>
        )}
        hint={readOnly
          ? "Available once your application is filled in"
          : agreed
            ? "A copy of the signed agreement is emailed to you. RushMyFood countersigns it when your application is approved."
            : "Check the box above to unlock Sign and submit."}
      />
    </StepCard>
  );
}
